import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTableAuth } from '../hooks/useTableAuth';
import { useApp } from '../context/AppContext';
import { useSelectedRestaurant } from '../context/SelectedRestaurantContext';
import { Category, MenuItem } from '../types';
import MenuItemCard from '../components/MenuItemCard';
import MenuItemDetailModal from '../components/MenuItemDetailModal';
import CartModal from '../components/CartModal';
import FloatingCart from '../components/FloatingCart';
import OrderQueueModal from '../components/OrderQueueModal';
import RestaurantSelector from '../components/RestaurantSelector';
import MenuSkeletonLoader from '../components/MenuSkeletonLoader';
import CategorySkeletonLoader from '../components/CategorySkeletonLoader';
import HeaderSkeletonLoader from '../components/HeaderSkeletonLoader';
import FloatingCartSkeleton from '../components/FloatingCartSkeleton';
import WaiterButtonSkeleton from '../components/WaiterButtonSkeleton';
import NotificationBanner from '../components/NotificationBanner';
import { storageService } from '../services/storage';
import { PWABottomRightBanner } from '../components/PWAFloatingBanner';
import { promotionsService } from '../services/promotions';
import { tableBillsService, TableBill } from '../services/tableBills';
import { api } from '../services/api';
import ClientTablesReservationModal from '../components/ClientTablesReservationModal';

// Header component with cart and table info
const MenuHeaderComponent = React.memo(({ readOnly = false, onOpenCart }: { readOnly?: boolean; onOpenCart?: () => void }) => {
  const { tableData } = useTableAuth();
  const { isLoading, selectedRestaurantLogo, selectedRestaurantName } = useSelectedRestaurant();
  const { isAuthenticated, storedUser } = useAuth();
  const { cart } = useApp();
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Mostrar skeleton si está cargando
  if (isLoading) {
    return <HeaderSkeletonLoader />;
  }

  return (
    <>
      <div className="bg-[#22B6B2] dark:bg-[#2C4358] px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Left section - Mesa info */}
          <div className="flex items-center">
            {tableData && (
              <span className="text-sm font-semibold text-white">
                Mesa {tableData.tableNumber}
              </span>
            )}
          </div>
          
          {/* Sección central: restaurante */}
          <div className="flex-1 mx-3 flex items-center justify-center">
            {(!isAuthenticated || storedUser?.currentRole === 'comensal') ? (
              <div className="flex items-center justify-center w-full">
                {/* Logo del restaurante */}
                {selectedRestaurantLogo && (
                  <img
                    src={selectedRestaurantLogo}
                    alt="Logo restaurante"
                    className="h-8 w-8 rounded-full object-cover mr-2"
                  />
                )}
                {/* Nombre del restaurante */}
                <span className="text-base font-bold text-white truncate">
                  {selectedRestaurantName || ''}
                </span>
                {readOnly && (
                  <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white border border-white/30">
                    Solo lectura
                  </span>
                )}
              </div>
            ) : ( 
              <RestaurantSelector 
                style={{ margin: 0 }}
                showLoading={isLoading}
                showInfo={false}
                isHeader={true}
              />
            )}
          </div>
          
          {/* Right section - Cart */}
          <div className="flex items-center">
            {!readOnly && (
              <button 
                className="relative bg-white/20 border border-white/30 rounded-full p-2 hover:bg-white/30 transition-colors"
                onClick={() => onOpenCart?.()}
              >
                <span className="text-lg text-white">🛒</span>
                {cartItemCount > 0 && (
                  <div className="absolute -top-1 -right-1 bg-white text-[#22B6B2] text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {cartItemCount}
                  </div>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

export default function MenuPage() {
  const navigate = useNavigate();
  const { 
    isAuthenticated, 
    tableData,
  } = useTableAuth();
  const { isOwner, isManager } = useAuth();
  const {
    categories,
    menuItems,
    loading: isMenuLoading,
    error: menuError,
    showDetailModal,
    showCartModal,
    loadCategories,
    loadMenu,
    requestWaiter,
    setShowDetailModal,
    setHideTabBar,
    setShowCartModal,
  } = useApp();

  const { setSelectedRestaurant, selectedRestaurantId, selectedRestaurantName } = useSelectedRestaurant();

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  const [validTableData, setValidTableData] = useState<{
    tableId: string;
    tableNumber: number;
    capacity: number;
    restaurantId?: string;
    restaurantName?: string;
  } | null>(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItem | null>(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [hasLoadedMenu, setHasLoadedMenu] = useState(false);
  const [allowPublicMenuOrders, setAllowPublicMenuOrders] = useState(false);
  const [showWaiterModal, setShowWaiterModal] = useState(false);
  const [showOrderQueue, setShowOrderQueue] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showClientTablesModal, setShowClientTablesModal] = useState(false);
  const [waiterRequestType, setWaiterRequestType] = useState<'assistance' | 'bill' | 'special_request'>('assistance');
  const [waiterMessage, setWaiterMessage] = useState('');
  const [hasProcessedURL, setHasProcessedURL] = useState(false); // Nueva bandera
  const location = useLocation();
  const urlRestaurantId = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(location.search);
    return params.get('restaurantId');
  }, [location.search]);
  const hasTableParamMemo = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(location.search);
    return params.has('table');
  }, [location.search]);

  // Si viene restaurantId por URL, consultar si permite pedidos sin QR
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!urlRestaurantId) {
        setAllowPublicMenuOrders(false);
        return;
      }
      try {
        const rest: any = await api.getRestaurantById(urlRestaurantId);
        // Asegurar que el header pueda mostrar nombre/logo en menú sin QR
        const restData = (rest as any)?.data || rest;
        const name = restData?.name || restData?.restaurantName || restData?.restaurant_name || '';
        const logo =
          restData?.image ||
          restData?.logo ||
          restData?.restaurantLogo ||
          restData?.restaurant_logo ||
          null;
        if (!cancelled) {
          await setSelectedRestaurant(urlRestaurantId, name, logo || undefined);
        }
        // Regla nueva: “Solo lectura” SOLO si el restaurante NO acepta pedidos off‑site.
        // La API a veces responde con { data: ... }, por eso tomamos de restData.
        const allow = restData?.allowPublicMenuOrders ?? restData?.allow_public_menu_orders;
        if (!cancelled) setAllowPublicMenuOrders(allow === true);
      } catch {
        if (!cancelled) setAllowPublicMenuOrders(false);
      }
    })();
    return () => { cancelled = true; };
  }, [urlRestaurantId, setSelectedRestaurant]);

  const [promoModal, setPromoModal] = useState<{open: boolean; imgs?: string[]; index?: number; title?: string; desc?: string; link?: string; ctaText?: string}>(
    { open: false }
  );
  const [promoPaused, setPromoPaused] = useState(false);

  // Detalle de Cuenta (comensal)
  const [billDetail, setBillDetail] = useState<TableBill | null>(null);
  const [showBillDetailModal, setShowBillDetailModal] = useState(false);
  const [loadingBillDetail, setLoadingBillDetail] = useState(false);

  const readOnly = useMemo(() => {
    // Solo operativo cuando viene el parámetro 'table' (escaneado) o hay datos de mesa activos
    // También permitir ordenar si viene restaurantId y el restaurante lo habilitó
    return !(tableData || hasTableParamMemo || (urlRestaurantId && allowPublicMenuOrders));
  }, [tableData, hasTableParamMemo, urlRestaurantId, allowPublicMenuOrders]);

  const hasTableContext = useMemo(() => {
    // “Menú con QR”: hay mesa activa o viene el parámetro table en la URL.
    // Si es menú sin QR (restaurantId sin table), NO mostrar acciones de cuenta/mesero.
    return Boolean(tableData || validTableData || hasTableParamMemo);
  }, [tableData, validTableData, hasTableParamMemo]);

  const isMenuWithoutQr = useMemo(() => {
    return Boolean(urlRestaurantId && !hasTableContext);
  }, [urlRestaurantId, hasTableContext]);

  // Memoizar las funciones para evitar recreaciones constantes
  const memoizedLoadCategories = useCallback((restaurantId?: string) => {
    loadCategories(restaurantId);
  }, [loadCategories]);

  const memoizedLoadMenu = useCallback((restaurantId?: string) => {
    loadMenu(restaurantId);
  }, [loadMenu]);

  // Validación simplificada y más rápida
  useEffect(() => {
    const validateTableAccess = async () => {
      setIsValidating(true);
      
      try {
        // Permitir acceso directo a owners y managers sin validación de mesa
        if (isOwner() || isManager()) {
          console.log('🍽️ Owner/Manager accediendo al menú sin validación de mesa');
          setValidTableData(null); // No necesitan datos de mesa
          setIsValidating(false);
          return;
        }
        
        // Para otros usuarios, validar acceso con mesa
        if (isAuthenticated && tableData) {
          setValidTableData(tableData);
          setIsValidating(false);
          return;
        }
        
        // Solo verificar storage si no hay datos en el hook
        const storageData = await storageService.getTableData();
        
        if (storageData) {
          setValidTableData(storageData);
          
          // Setear restaurante global también cuando se carga desde storage
          setSelectedRestaurant(storageData.restaurantId, storageData.restaurantName || '', storageData.restaurantLogo);
        } else if (urlRestaurantId) {
          // Solo lectura por restaurantId: no hay mesa
          setValidTableData(null);
          // No sobrescribir nombre/logo si ya se cargaron (evita header vacío)
          if (selectedRestaurantId !== urlRestaurantId) {
            setSelectedRestaurant(urlRestaurantId, '');
          }
          setShouldRedirect(false);
        } else {
          // Marcar que se debe redirigir
          console.log('❌ No hay datos de mesa válidos');
          setShouldRedirect(true);
        }
        
      } catch (error) {
        console.error('Error validando acceso a mesa:', error);
      } finally {
        setIsValidating(false);
      }
    };

    // Solo validar si no estamos cargando autenticación
    validateTableAccess();
  }, [isAuthenticated, tableData, isOwner, isManager, setSelectedRestaurant, urlRestaurantId, selectedRestaurantId]);

  // Cargar datos del menú inmediatamente cuando tengamos datos válidos o sea owner/manager
  useEffect(() => {
    const shouldLoadMenu = validTableData || isOwner() || isManager() || urlRestaurantId;
    
    if (shouldLoadMenu && !hasLoadedMenu) {
      setHasLoadedMenu(true);
      
      if (validTableData) {
        // Para usuarios con mesa, cargar del restaurante de la mesa
        memoizedLoadCategories(validTableData.restaurantId);
        memoizedLoadMenu(validTableData.restaurantId);
      } else if (urlRestaurantId) {
        // Solo lectura: cargar por restaurantId
        memoizedLoadCategories(urlRestaurantId);
        memoizedLoadMenu(urlRestaurantId);
      } else {
        // Para owners/managers, cargar del restaurante seleccionado globalmente
        console.log('🍽️ Owner/Manager cargando menú del restaurante seleccionado globalmente');
        memoizedLoadCategories();
        memoizedLoadMenu();
      }
    }
  }, [validTableData, memoizedLoadCategories, memoizedLoadMenu, hasLoadedMenu, isOwner, isManager, urlRestaurantId]);

  // Mostrar promoción activa al entrar (una vez por día por restaurante)
  useEffect(() => {
    const showPromoIfAny = async () => {
      const restId = validTableData?.restaurantId || urlRestaurantId || selectedRestaurantId;
      if (!restId) return;
      try {
        const resp: any = await promotionsService.listActive(restId);
        const data: any[] = Array.isArray(resp?.data?.data) ? resp.data.data : (resp?.data || []);
        console.log('🎯 Promos activas recibidas:', data);
        // Filtrar por ventana horaria y por snooze individual de cada promo
        const eligible = data.filter((p: any) => {
          // Ventana horaria por calendario (start_at / end_at)
          const nowTs = Date.now();
          const startOk = !p.startAt || new Date(p.startAt).getTime() <= nowTs;
          const endOk = !p.endAt || new Date(p.endAt).getTime() >= nowTs;
          if (!(startOk && endOk)) return false;
          // snooze individual
          const pKey = `promo_snooze_until_${restId}_${p.id}`;
          const until = localStorage.getItem(pKey);
          if (until && new Date(until).getTime() > Date.now()) return false;
          return true;
        });
        if (eligible.length === 0) {
          console.log('ℹ️ No hay promos elegibles para mostrar ahora');
          return;
        }
        // Ordenar por prioridad (2 alta, 1 media, 0 baja) descendente
        eligible.sort((a: any, b: any) => (b?.config?.priority ?? 1) - (a?.config?.priority ?? 1));
        // Rotación: ciclo por índice almacenado
        const idxKey = `promo_cycle_idx_${restId}`;
        const idx = parseInt(localStorage.getItem(idxKey) || '-1', 10);
        const nextIdx = ((isNaN(idx) ? -1 : idx) + 1) % eligible.length;
        localStorage.setItem(idxKey, String(nextIdx));
        const p = eligible[nextIdx];
        const imgs = (() => {
          const im: any = (p as any).images;
          if (!im) return [] as string[];
          if (Array.isArray(im)) {
            return im.map((x: any) => (typeof x === 'string' ? x : x?.url)).filter(Boolean);
          }
          if (typeof im === 'object') {
            if (Array.isArray((im as any).urls)) return (im as any).urls.filter(Boolean);
            if (typeof (im as any).url === 'string') return [(im as any).url];
          }
          return [] as string[];
        })();
        setPromoModal({ open: true, imgs: imgs.length ? imgs : undefined, index: 0, title: p.title, desc: p.description, link: p.config?.link, ctaText: p.config?.ctaText || 'Ver promoción' });
        console.log('✅ Abriendo modal de promo:', { title: p.title, imgsCount: imgs.length });
        const days = Math.max(1, parseInt(p?.config?.snoozeDays ?? '7', 10));
        const until = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
        const pSnoozeKey = `promo_snooze_until_${restId}_${p.id}`;
        localStorage.setItem(pSnoozeKey, until);
      } catch {}
    };
    showPromoIfAny();
  }, [validTableData?.restaurantId, urlRestaurantId, selectedRestaurantId]);

  // Auto-slide del carrusel (4s) con pausa en interacción
  useEffect(() => {
    if (!promoModal.open || !promoModal.imgs || promoModal.imgs.length <= 1) return;
    if (promoPaused) return;
    const id = setInterval(() => {
      setPromoModal(m => {
        const len = m.imgs?.length || 1;
        const next = (((m.index || 0) + 1) % len);
        return { ...m, index: next };
      });
    }, 4000);
    return () => clearInterval(id);
  }, [promoModal.open, promoModal.imgs?.length, promoPaused]);

  // Procesar parámetro table de la URL cuando no hay datos de mesa
  useEffect(() => {
    const tryProcessTableFromURL = async () => {
      if (typeof window !== 'undefined' && !hasProcessedURL) {
        const url = window.location.href;
        const urlObj = new URL(url);
        const tableParam = urlObj.searchParams.get('table');
        
        if (tableParam && !hasProcessedURL) { // Solo procesar si no se ha procesado antes
          try {
            console.log('🔍 MenuPage - Procesando parámetro table de la URL');
            const decodedData = atob(tableParam);
            console.log('🔍 MenuPage - Decoded data:', decodedData);
            
            // Parse el JSON para obtener el token_mesa
            const qrData = JSON.parse(decodedData);
            console.log('🔍 MenuPage - QR Data:', qrData);
            
            if (!qrData.token_mesa) {
              throw new Error('No se encontró el token de mesa en el QR');
            }
            
            // Decodificar el JWT para obtener los datos de la mesa
            const jwtPayload = JSON.parse(atob(qrData.token_mesa.token.split('.')[1]));
            console.log('🔍 MenuPage - JWT Payload:', jwtPayload);
            
            // Crear objeto tableData con los datos del JWT + el JWT completo
            const tableData = {
              tableId: jwtPayload.id,
              tableNumber: jwtPayload.number,
              capacity: jwtPayload.capacity,
              location: jwtPayload.location,
              status: jwtPayload.status,
              restaurantId: jwtPayload.restaurantId,
              restaurantName: jwtPayload.restaurantName,
              restaurantLogo: jwtPayload.restaurantLogo,
              createdAt: jwtPayload.createdAt || new Date().toISOString(),
              tableSigned: qrData.token_mesa.token // JWT completo para enviar al backend
            };
            console.log('🔍 MenuPage - Datos de mesa procesados:', tableData);
            
            // Verificar si ya se procesó esta URL específica
            const processedURLs = JSON.parse(localStorage.getItem('processedURLs') || '[]');
            const currentURL = window.location.href;
            
            if (processedURLs.includes(currentURL)) {
              console.log('🔍 MenuPage - URL ya procesada anteriormente, saltando...');
              setHasProcessedURL(true);
              return;
            }
            
            const existingTableData = await storageService.getTableData();
            const isSameTable = existingTableData &&
              existingTableData.restaurantId === tableData.restaurantId &&
              existingTableData.tableId === tableData.tableId;

            if (isSameTable) {
              setValidTableData(existingTableData);
              setShouldRedirect(false);
              setHasProcessedURL(true);
              return;
            }

            // Guardar usando cleanup para cambiar de mesa/restaurante
            await storageService.saveTableDataWithCleanup(tableData);
            
            // Setear restaurante global
            setSelectedRestaurant(tableData.restaurantId, tableData.restaurantName || '', tableData.restaurantLogo);
            
            // Marcar esta URL como procesada
            processedURLs.push(currentURL);
            localStorage.setItem('processedURLs', JSON.stringify(processedURLs));
            
            // Mensaje de éxito omitido para evitar alert; el UI ya refleja los datos cargados
            
            // En lugar de recargar, actualizar el estado directamente
            setValidTableData(tableData);
            setShouldRedirect(false);
            setHasProcessedURL(true); // Marcar que ya se procesó la URL
          } catch (error) {
            console.error('Error procesando URL de mesa:', error);
            // Si falla, mostrar modal de error amigable
            // Nota: esta pantalla es pública; no exponemos detalles sensibles
            // Se puede integrar ErrorModal global si esta pantalla también lo usa
          }
        }
      }
    };
    tryProcessTableFromURL();
  }, [tableData, validTableData, hasProcessedURL, setSelectedRestaurant]);

  // Limpiar la bandera cuando cambie la URL
  useEffect(() => {
    const handleURLChange = () => {
      setHasProcessedURL(false);
    };
    
    // Escuchar cambios en la URL
    window.addEventListener('popstate', handleURLChange);
    
    return () => {
      window.removeEventListener('popstate', handleURLChange);
    };
  }, []);

  // Reset del flag de carga cuando cambie el restaurante para owners/managers
  useEffect(() => {
    if (isOwner() || isManager()) {
      setHasLoadedMenu(false);
    }
  }, [isOwner, isManager]);

  // Asegurar que la TabBar se muestre cuando se desmonte el componente
  useEffect(() => {
    return () => {
      setHideTabBar(false); // Mostrar TabBar cuando se desmonte el componente
    };
  }, [setHideTabBar]);

  // Efecto para manejar redirección de manera segura
  useEffect(() => {
    if (shouldRedirect && !isValidating) {
      // Verificar roles antes de redirigir
      const isAdmin = isOwner() || isManager();
      
      // Verificar si hay parámetro table en la URL (no redirigir si lo hay)
      const hasTableParam = typeof window !== 'undefined' && window.location.href.includes('table=');
      
      if (!isAdmin && !hasTableParam) {
        // Usar un pequeño delay para asegurar que el layout esté montado
        const timer = setTimeout(() => {
          navigate('/');
        }, 500);
        
        return () => clearTimeout(timer);
      } else {
        console.log('🔍 MenuPage - No redirigiendo porque:', {
          isAdmin,
          hasTableParam
        });
      }
    }
  }, [shouldRedirect, isValidating, isOwner, isManager, navigate]);

  const handleCategoryPress = (category: Category) => {
    setSelectedCategory(category.id);
  };

  const handleShowAll = () => {
    setSelectedCategory(null);
  };

  const handleViewDetails = (item: MenuItem) => {
    const isPlanLimited = item.status === 'plan_limited' || (typeof item.status === 'string' && item.status.toLowerCase().includes('plan'));
    if (isPlanLimited) {
      return;
    }
    setSelectedMenuItem(item);
    setShowDetailModal(true);
    setHideTabBar(true); // Ocultar TabBar cuando se abre el modal
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedMenuItem(null);
    setHideTabBar(false); // Mostrar TabBar cuando se cierra el modal
  };

  const handleCallWaiter = () => {
    setShowWaiterModal(true);
    setHideTabBar(true); // Ocultar TabBar cuando se abre el modal
  };

  const handleSubmitWaiterRequest = async () => {
    try {
      await requestWaiter(waiterRequestType, waiterMessage);
      setShowWaiterModal(false);
      setWaiterMessage('');
      setHideTabBar(false); // Mostrar TabBar cuando se cierra el modal
      // Éxito silencioso
    } catch (error) {
      console.error('Error enviando solicitud al mesero:', error);
      // Para esta pantalla pública usamos un mensaje simple en consola; si quieres, integro ErrorModal aquí también
    }
  };

  const handleCloseWaiterModal = () => {
    setShowWaiterModal(false);
    setWaiterMessage('');
    setHideTabBar(false); // Mostrar TabBar cuando se cierra el modal
  };

  const handleOpenOrderQueue = () => {
    setShowOrderQueue(true);
    setHideTabBar(true);
  };

  const handleCloseOrderQueue = () => {
    setShowOrderQueue(false);
    setHideTabBar(false);
  };

  // Abrir Detalle de Cuenta (cuenta abierta de la mesa)
  const handleOpenBillDetail = useCallback(async () => {
    try {
      if (!selectedRestaurantId || !tableData?.tableId) {
        alert('No se pudo identificar la mesa o restaurante');
        return;
      }
      setLoadingBillDetail(true);
      const detail = await tableBillsService.getOpenTableBill(selectedRestaurantId, tableData.tableId);
      if (!detail) {
        alert('No hay una cuenta abierta para esta mesa');
        return;
      }
      setBillDetail(detail);
      setShowBillDetailModal(true);
    } catch (error) {
      console.error('Error cargando detalle de cuenta:', error);
      alert('No se pudo cargar el detalle de la cuenta');
    } finally {
      setLoadingBillDetail(false);
    }
  }, [selectedRestaurantId, tableData?.tableId]);

  // const handleOpenProfile = () => {
  //   navigate('/home/profile');
  // };

  const handleShowCart = () => {
    setShowCartModal(true);
    setHideTabBar(true); // Ocultar TabBar cuando se abre el modal
  };

  const handleCloseCart = () => {
    setShowCartModal(false);
    setHideTabBar(false); // Mostrar TabBar cuando se cierra el modal
  };

  const handlePlaceOrder = () => {
    // Cerrar carrito y abrir inmediatamente el estado de orden
    setShowCartModal(false);
    handleOpenOrderQueue();
  };

  // Mostrar skeleton loader mientras se valida o procesa URL
  if (isValidating) {
    return <MenuSkeletonLoader />;
  }

  // Mostrar mensaje de redirección si es necesario
  const hasTableParam = typeof window !== 'undefined' && window.location.href.includes('table=');
  if (shouldRedirect && !isOwner() && !isManager() && !hasTableParam) {
    return (
      <div className="min-h-screen bg-[#e9ecef] flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#22B6B2] mx-auto mb-4"></div>
          <p className="text-[#2C4358] text-lg">Redirigiendo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#e9ecef]">
      {/* Banner de Notificaciones */}
      <NotificationBanner 
        position="top"
        onPermissionGranted={() => console.log('✅ Notificaciones habilitadas en MenuPage')}
        onPermissionDenied={() => console.log('❌ Permisos denegados en MenuPage')}
      />
      
      {/* Header con información del restaurante y carrito */}
      <MenuHeaderComponent readOnly={readOnly} onOpenCart={handleShowCart} />
      
      {/* Controles de categorías */}
      {isMenuLoading && categories.length === 0 ? (
        <CategorySkeletonLoader />
      ) : (
        <div className="bg-[#f8f9fa] border-b border-[#dee2e6]">
          <div className="flex overflow-x-auto space-x-3 p-4 pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <button
              className={`flex-shrink-0 px-4 py-3 rounded-2xl text-center min-w-24 transition-all duration-300 hover:scale-105 focus:outline-none ${
                !selectedCategory 
                  ? 'shadow-lg border-2' 
                  : 'bg-white hover:shadow-md border border-gray-100'
              }`}
              style={{
                backgroundColor: !selectedCategory ? '#22B6B2' + '20' : 'white',
                borderColor: !selectedCategory ? '#22B6B2' + '40' : '#e5e7eb',
                boxShadow: !selectedCategory ? `0 4px 12px #22B6B230` : '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onClick={handleShowAll}
            >
              <div 
                className="text-xl mb-1 transition-transform duration-300"
                style={{ 
                  transform: !selectedCategory ? 'scale(1.1)' : 'scale(1)',
                  color: !selectedCategory ? '#22B6B2' : '#2C4358'
                }}
              >
                🍴
              </div>
              <div 
                className="text-xs font-semibold leading-tight transition-colors duration-300"
                style={{ 
                  color: !selectedCategory ? '#22B6B2' : '#2C4358'
                }}
              >
                Todos ({menuItems.length})
              </div>
            </button>
            
            {categories.map((category) => {
              const categoryItems = menuItems.filter(item => item.categoryId === category.id || item.category === category.id);
              const isSelected = selectedCategory === category.id;
              
              return (
                <button
                  key={category.id}
                  className={`flex-shrink-0 px-4 py-3 rounded-2xl text-center min-w-24 transition-all duration-300 hover:scale-105 focus:outline-none ${
                    isSelected 
                      ? 'shadow-lg border-2' 
                      : 'bg-white hover:shadow-md border border-gray-100'
                  }`}
                  style={{
                    backgroundColor: isSelected ? category.color + '20' : 'white',
                    borderColor: isSelected ? category.color + '40' : '#e5e7eb',
                    boxShadow: isSelected ? `0 4px 12px ${category.color}30` : '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  onClick={() => handleCategoryPress(category)}
                >
                  <div 
                    className="text-xl mb-1 transition-transform duration-300"
                    style={{ 
                      transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                      color: category.color
                    }}
                  >
                    {category.icon}
                  </div>
                  <div 
                    className="text-xs font-bold leading-tight category-text transition-colors duration-300"
                    style={{ 
                      color: isSelected ? category.color : '#2C4358'
                    }}
                  >
                    {category.name} ({categoryItems.length})
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Contenido principal */}
      <div className="bg-[#e9ecef] flex-1">
        {isMenuLoading ? (
          <div className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
                  <div className="h-32 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                    <div className="flex justify-between items-center">
                      <div className="w-16 h-4 bg-gray-200 rounded"></div>
                      <div className="w-20 h-6 bg-[#22B6B2] rounded"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : menuError ? (
          <div className="flex flex-col items-center justify-center py-20 px-5">
            <p className="text-[#FF6B6B] text-center mb-4">{menuError}</p>
            <button 
              className="bg-[#22B6B2] text-white px-5 py-2 rounded-lg font-semibold"
              onClick={() => {
                if (selectedCategory) {
                  // local filter view; no remote fetch
                } else {
                  loadMenu();
                }
              }}
            >
              Reintentar
            </button>
          </div>
        ) : menuItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-5">
            <div className="text-5xl mb-4">🍽️</div>
            <h3 className="text-lg font-bold text-[#2C4358] mb-2 text-center">
              No hay elementos en el menú.
              <div className="flex flex-col items-center mt-4">
                <img 
                  src="/assets/images/qr_placeholder.png" 
                  alt="Escanea el QR de la mesa" 
                  className="w-40 h-48 mb-3 opacity-80"
                  style={{ filter: 'drop-shadow(0 2px 8px rgba(34,182,178,0.15))' }}
                />
                <span className="text-base text-[#22B6B2] font-semibold text-center">
                  Escanea el QR de la mesa con la cámara de tu teléfono para ver el menú.
                </span>
              </div>
            </h3>
            <p className="text-[#85929e] text-center">
              {selectedCategory ? 'Prueba con otra categoría' : ''}
            </p>
                            <a href="/profile">ir a mi perfil</a>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-5">
            {(selectedCategory
              ? menuItems.filter(item => item.categoryId === selectedCategory || item.category === selectedCategory)
              : menuItems
            ).map((item) => (
              <MenuItemCard 
                key={item.id} 
                item={item} 
                onViewDetails={handleViewDetails} 
                readOnly={readOnly}
              />
            ))}
          </div>
        )}
      </div>

      {/* Botón flotante para llamar al mesero */}
      {isMenuLoading ? (
        <WaiterButtonSkeleton />
      ) : (
        <>
          {/* FAB principal */}
          {!readOnly && (
          <button
            className={`fixed left-5 ${showQuickActions ? 'bottom-14' : 'bottom-3'} w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-white transition-all duration-300 ${showQuickActions ? 'bg-[#2C4358]' : 'animated-gradient'} z-50 fab-breathe ring-1 ring-white/20`}
            onClick={() => setShowQuickActions(v => !v)}
            aria-label="Acciones rápidas"
            title="Acciones rápidas"
          >
            <span className={`text-2xl transition-transform duration-300 ${showQuickActions ? '' : 'dot-pulse'}`}>{showQuickActions ? '✕' : '⋯'}</span>
          </button>
          )}

          {/* Overlay para cerrar al tocar fuera */}
          {!readOnly && showQuickActions && (
            <div className="fixed inset-0 z-40" onClick={() => setShowQuickActions(false)} />
          )}

          {/* Botones secundarios animados */}
          {!readOnly && showQuickActions && (
            <div className="fixed left-5 bottom-4 flex flex-col items-start space-y-3 transition-all duration-300 z-50">
              <button
                className="bg-white text-[#2C4358] border border-[#22B6B2]/30 rounded-full py-2 px-4 flex items-center shadow-lg hover:bg-[#f8fafc] transition-colors"
                onClick={handleOpenOrderQueue}
              >
                <span className="text-xl mr-2">📋</span>
                <span className="text-sm font-bold">Estado de orden</span>
              </button>
              {isMenuWithoutQr && (
                <button
                  className="bg-white text-[#2C4358] border border-[#22B6B2]/30 rounded-full py-2 px-4 flex items-center shadow-lg hover:bg-[#f8fafc] transition-colors"
                  onClick={() => setShowClientTablesModal(true)}
                >
                  <span className="text-xl mr-2">🍽️</span>
                  <span className="text-sm font-bold">Ver mesas</span>
                </button>
              )}
              {hasTableContext && (
                <>
                  <button
                    className={`bg-white text-[#2C4358] border border-[#22B6B2]/30 rounded-full py-2 px-4 flex items-center shadow-lg transition-colors ${loadingBillDetail ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[#f8fafc]'}`}
                    onClick={handleOpenBillDetail}
                    disabled={loadingBillDetail}
                  >
                    <span className="text-xl mr-2">🧾</span>
                    <span className="text-sm font-bold">{loadingBillDetail ? 'Cargando...' : 'Cuenta'}</span>
                  </button>
                  <button
                    className="bg-white text-[#2C4358] border border-[#22B6B2]/30 rounded-full py-2 px-4 flex items-center shadow-lg hover:bg-[#f8fafc] transition-colors"
                    onClick={handleCallWaiter}
                  >
                    <span className="text-xl mr-2">👋</span>
                    <span className="text-sm font-bold">Mesero</span>
                  </button>
                </>
              )}
              <button
                className="bg-white text-[#2C4358] border border-[#22B6B2]/30 rounded-full py-2 px-4 flex items-center shadow-lg hover:bg-[#f8fafc] transition-colors"
                onClick={() => navigate('/profile')}
              >
                <span className="text-xl mr-2">👤</span>
                <span className="text-sm font-bold">Perfil</span>
              </button>
            </div>
          )}
        </>
      )}

      {/* Animación de respiración para FAB */}
      <style>{`
        @keyframes fabBreath {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 12px 28px rgba(34, 182, 178, 0.20);
          }
          50% {
            transform: scale(1.08);
            box-shadow: 0 16px 36px rgba(34, 182, 178, 0.35);
          }
        }
        .fab-breathe {
          animation: fabBreath 2.2s ease-in-out infinite;
        }
        /* Gradiente animado para fondo del FAB */
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        /* Sutil brillo extra alrededor del FAB */
        @keyframes glowPulse {
          0% {
            box-shadow:
              0 12px 28px rgba(34, 182, 178, 0.20),
              0 0 0 0 rgba(34, 182, 178, 0.0);
            filter: brightness(1);
          }
          50% {
            box-shadow:
              0 18px 40px rgba(34, 182, 178, 0.45),
              0 0 16px 6px rgba(34, 182, 178, 0.25);
            filter: brightness(1.06);
          }
          100% {
            box-shadow:
              0 12px 28px rgba(34, 182, 178, 0.20),
              0 0 0 0 rgba(34, 182, 178, 0.0);
            filter: brightness(1);
          }
        }
        /* Flotación suave vertical */
        @keyframes floatY {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
        }
        /* Pulso de los tres puntos cuando está cerrado */
        @keyframes dotPulse {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.95; }
          50% { transform: translateY(-1px) scale(1.06); opacity: 1; }
        }
        .animated-gradient {
          background: linear-gradient(135deg, #22B6B2, #1ec9c5, #2C4358, #22B6B2);
          background-size: 300% 300%;
          animation:
            fabBreath 2.2s ease-in-out infinite,
            gradientShift 7s ease-in-out infinite,
            glowPulse 3.2s ease-in-out infinite,
            floatY 4s ease-in-out infinite;
        }
        .dot-pulse {
          animation: dotPulse 1.6s ease-in-out infinite;
        }
      `}</style>

      {/* Detail Modal */}
      <MenuItemDetailModal
        visible={showDetailModal}
        onClose={handleCloseDetailModal}
        item={selectedMenuItem}
        readOnly={readOnly}
      />

      {/* Modal para llamar al mesero */}
      {showWaiterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-5">
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl p-6 w-full sm:max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={handleCloseWaiterModal}
              aria-label="Cerrar"
              title="Cerrar"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-white"
            >
              ✕
            </button>
            <div className="pt-6 sm:pt-0">
              <div className="text-lg font-bold text-[#2C4358] mb-4 sm:hidden">🗣️ Llamar al Mesero</div>
            </div>
            
            <div className="mb-5">
              <label className="block text-base font-bold text-[#2C4358] mb-3">
                Tipo de solicitud:
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  className={`p-3 rounded-lg border text-center ${
                    waiterRequestType === 'assistance' 
                      ? 'bg-[#22B6B2] border-[#22B6B2] text-white' 
                      : 'bg-[#f8f9fa] border-[#dee2e6] text-[#85929e]'
                  }`}
                  onClick={() => setWaiterRequestType('assistance')}
                >
                  <div className="text-xl mb-1">🤝</div>
                  <div className="text-xs font-semibold">Asistencia</div>
                </button>
                
                <button
                  className={`p-3 rounded-lg border text-center ${
                    waiterRequestType === 'bill' 
                      ? 'bg-[#22B6B2] border-[#22B6B2] text-white' 
                      : 'bg-[#f8f9fa] border-[#dee2e6] text-[#85929e]'
                  }`}
                  onClick={() => setWaiterRequestType('bill')}
                >
                  <div className="text-xl mb-1">🧾</div>
                  <div className="text-xs font-semibold">Cuenta</div>
                </button>
                
                <button
                  className={`p-3 rounded-lg border text-center ${
                    waiterRequestType === 'special_request' 
                      ? 'bg-[#22B6B2] border-[#22B6B2] text-white' 
                      : 'bg-[#f8f9fa] border-[#dee2e6] text-[#85929e]'
                  }`}
                  onClick={() => setWaiterRequestType('special_request')}
                >
                  <div className="text-xl mb-1">💬</div>
                  <div className="text-xs font-semibold">Solicitud Especial</div>
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-bold text-[#2C4358] mb-2">
                Mensaje (opcional):
              </label>
              <textarea
                className="w-full border border-[#dee2e6] rounded-lg p-3 text-sm bg-[#f8f9fa] text-[#2C4358] min-h-20 resize-none"
                value={waiterMessage}
                onChange={(e) => setWaiterMessage(e.target.value)}
                placeholder="Ej: Necesito más servilletas, ¿tienen postres?"
                rows={3}
              />
            </div>

            <div className="flex space-x-3">
              <button
                className="flex-1 bg-[#6B7280] text-white py-3 rounded-lg font-bold"
                onClick={handleCloseWaiterModal}
              >
                Cancelar
              </button>
              <button
                className="flex-1 bg-[#22B6B2] text-white py-3 rounded-lg font-bold"
                onClick={handleSubmitWaiterRequest}
              >
                Llamar Mesero
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalle de Cuenta (Comensal) */}
      {showBillDetailModal && billDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-5">
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl p-6 w-full sm:max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setShowBillDetailModal(false)}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-white"
              aria-label="Cerrar"
              title="Cerrar"
            >
              ✕
            </button>
            <div className="pt-6 sm:pt-0 mb-4">
              <div className="text-lg font-bold text-[#2C4358] sm:hidden">
                Detalles - Mesa {billDetail.tableNumber}
              </div>
              <div className="hidden sm:flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl font-bold text-[#2C4358]">
                    Detalles - Mesa {billDetail.tableNumber}
                  </h2>
                  <div className="text-sm text-[#85929e]">
                    {(billDetail as any).restaurantName || ''}{billDetail.waiterName ? ` · Mesero: ${billDetail.waiterName}` : ''}
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  billDetail.status === 'paid' ? 'bg-emerald-100 text-emerald-700'
                  : billDetail.status === 'closed' ? 'bg-orange-100 text-orange-700'
                  : 'bg-green-100 text-green-700'
                }`}>
                  {billDetail.status === 'paid' ? 'Pagada' : billDetail.status === 'closed' ? 'Cerrada' : 'Abierta'}
                </span>
              </div>
            </div>

            {(() => {
              const toNum = (v: any) => typeof v === 'string' ? parseFloat(v) : (v || 0);
              const computedBillTotal = (billDetail.orders || []).reduce((sum: number, o: any) => {
                const val = o?.totalAmount ?? o?.total_amount;
                return sum + toNum(val);
              }, 0);
              const displayTotal = billDetail.totalAmount > 0 ? billDetail.totalAmount : computedBillTotal;
              return (
                <div className="bg-[#f8f9fa] rounded-xl p-4 mb-4 border border-[#e9ecef]">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-[#85929e]">Órdenes: {billDetail.orders?.length ?? 0}</div>
                    <div className="text-lg font-black text-green-600">{`$${displayTotal.toFixed(2)}`}</div>
                  </div>
                  {billDetail.notes && (
                    <div className="mt-2 text-sm text-[#2C4358]">
                      <span className="font-semibold">Notas: </span>{billDetail.notes}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="space-y-5">
              {(billDetail.orders || []).map((order: any, idx: number) => {
                const toNum = (v: any) => typeof v === 'string' ? parseFloat(v) : (v || 0);
                const orderTotal = toNum(order.totalAmount ?? order.total_amount);
                return (
                  <div key={order.id || idx} className="border border-[#e9ecef] rounded-xl overflow-hidden">
                    <div className="px-4 py-3 bg-[#f8f9fa] border-b border-[#e9ecef] flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="font-bold text-[#2C4358]">
                          Orden {order.id?.slice?.(0, 6) || (idx + 1)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-[#85929e]">{new Date(order.createdAt).toLocaleTimeString()}</div>
                        <div className="text-sm text-[#2C4358]">
                          Total: <span className="font-bold text-green-600">{`$${orderTotal.toFixed(2)}`}</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      {(order.items || []).map((item: any, i: number) => {
                        const qty = item.quantity ?? 1;
                        const priceVal = toNum(item.price);
                        const lineTotal = qty * priceVal;
                        const name = item.menuItemName ?? item.menu_item_name ?? item.name ?? 'Item';
                        const notes = item.notes ?? item.specialInstructions ?? '';
                        const size = item.size ?? '';
                        return (
                          <div key={item.id || i} className="flex items-center justify-between bg-[#f8f9fa] rounded-lg px-3 py-2">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-[#22B6B2] rounded-full flex items-center justify-center text-white font-bold">
                                {qty}
                              </div>
                              <div>
                                <div className="font-medium text-[#2C4358]">
                                  {name}{size ? ` (${size})` : ''}
                                </div>
                                {notes && (
                                  <div className="text-sm text-[#85929e]">Notas: {notes}</div>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-[#85929e]">{`$${priceVal.toFixed(2)}`}</div>
                              <div className="font-bold text-green-600">{`$${lineTotal.toFixed(2)}`}</div>
                            </div>
                          </div>
                        );
                      })}
                      {(order.items || []).length === 0 && (
                        <div className="text-center text-[#85929e] text-sm">Sin items</div>
                      )}
                    </div>
                  </div>
                );
              })}
              {(billDetail.orders || []).length === 0 && (
                <div className="text-center text-[#85929e] text-sm">Esta cuenta aún no tiene órdenes</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Mesas (cliente) para menú sin QR */}
      <ClientTablesReservationModal
        visible={showClientTablesModal}
        onClose={() => setShowClientTablesModal(false)}
        restaurantId={urlRestaurantId || selectedRestaurantId || ''}
        restaurantName={selectedRestaurantName}
      />

      {/* Modal de promoción */}
      {promoModal.open && (
        <div className="fixed inset-0 bg-black/50 z-[9998] flex items-center justify-center p-4" onClick={() => setPromoModal({ open: false })}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {promoModal.imgs && promoModal.imgs.length > 0 && (
              <div 
                className="relative"
                onMouseEnter={() => setPromoPaused(true)}
                onMouseLeave={() => setPromoPaused(false)}
                onTouchStart={() => setPromoPaused(true)}
                onTouchEnd={() => setTimeout(() => setPromoPaused(false), 1000)}
              >
                <img src={promoModal.imgs[promoModal.index || 0]} alt={promoModal.title || ''} className="w-full h-56 object-cover" />
                {promoModal.imgs.length > 1 && (
                  <>
                    <button onClick={() => setPromoModal(m => ({ ...m, index: Math.max(0, ((m.index || 0) - 1 + (m.imgs?.length || 1)) % (m.imgs?.length || 1)) }))} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full w-8 h-8">‹</button>
                    <button onClick={() => setPromoModal(m => ({ ...m, index: (((m.index || 0) + 1) % (m.imgs?.length || 1)) }))} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full w-8 h-8">›</button>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                      {promoModal.imgs.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setPromoModal(m => ({ ...m, index: i }))}
                          className={`w-2 h-2 rounded-full ${((promoModal.index || 0) === i) ? 'bg-white' : 'bg-white/50'}`}
                          aria-label={`Ir a imagen ${i + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="p-4">
              {promoModal.title && <h3 className="text-lg font-bold text-[#2C4358] mb-1">{promoModal.title}</h3>}
              {promoModal.desc && <p className="text-sm text-[#2C4358] opacity-80">{promoModal.desc}</p>}
              <div className="flex justify-end gap-2 mt-4">
                <button className="px-3 py-2 text-sm rounded-lg border border-gray-200" onClick={() => setPromoModal({ open: false })}>Cerrar</button>
                {promoModal.link && (
                  <a href={promoModal.link} target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm rounded-lg bg-[#22B6B2] text-white font-semibold hover:opacity-90">{promoModal.ctaText || 'Ver promoción'}</a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de estado de orden (cola) */}
      <OrderQueueModal
        visible={showOrderQueue}
        onClose={handleCloseOrderQueue}
        restaurantId={validTableData?.restaurantId}
        tableId={validTableData?.tableId}
        tableNumber={validTableData?.tableNumber}
      />

      {/* Cart Modal */}
      {!readOnly && (
        <CartModal
          visible={showCartModal}
          onClose={handleCloseCart}
          onPlaceOrder={handlePlaceOrder}
        />
      )}

      {/* Floating Cart */}
      {isMenuLoading ? (
        <FloatingCartSkeleton />
      ) : (
        !readOnly && <FloatingCart onOpenCart={handleShowCart} />
      )}

      {/* Banner PWA Flotante */}
      <PWABottomRightBanner />
    </div>
  );
} 